import { ExpressionValue } from "./ExpressionValue";
import { DataUtilities } from "../Common/DataUtilities";
import { ExecutionData } from "../Common/Data/ExecutionData/ExecutionData";
import { IConfigStorage } from "../Common/IConfigStorage";
import { IDataDefinition } from "../Common/Data/Definitions/IDataDefinition";
import { NumberDataDefinition } from "../Common/Data/Definitions/NumberDataDefinition";
import { ValidationIssue } from "../Common/IDesignValidationProvider";
import { ITestingContext } from "../Common/Testing/ITestingContext";
import { ExpressionValueType } from "./ExpressionValueType";
import { IBaseExpressionElement } from "./IBaseExpressionElement";
import { ExpressionContext } from "./ExpressionContext";
import type { AppGlueAny } from "../Common/AppGlueAny";

export abstract class ExpressionRule implements IBaseExpressionElement, IConfigStorage {
    // this is optional, but is the name that is in the toolbox that might be different than type.
    readonly __ruleName?: string;
    // this is the key for a rule.
    abstract readonly __type: string;
    readonly canSelect: boolean = true;
    _id: string = DataUtilities.generateUniqueId();
    // non serialized
    expressionContext?: ExpressionContext;
    private values: ExpressionValue[] = [];

    protected _expressionValueType: IDataDefinition = new NumberDataDefinition();

    get expressionValueType(): IDataDefinition {
        return this._expressionValueType;
    }

    set expressionValueType(expressionValueType: IDataDefinition) {
        this._expressionValueType = expressionValueType;
    }

    abstract evaluate(data: ExecutionData, testContext: ITestingContext | undefined): AppGlueAny;

    setExpressionContext(expressionContext1: ExpressionContext, owner: IBaseExpressionElement): void {
        this.expressionContext = expressionContext1;

        expressionContext1.register(this, owner);

        for (let v of this.values) {
            v.setExpressionContext(expressionContext1, this);
        }
    }

    setEditContextWithoutRegister(editContext: ExpressionContext): void {
        this.expressionContext = editContext;

        for (let v of this.values) {
            v.setExpressionContext(editContext, this);
        }
    }

    getExpressionValues(): ExpressionValue[] {
        return this.values;
    }

    getAllExpressionValues(): ExpressionValue[] {
        let values: ExpressionValue[] = [...this.getExpressionValues()];

        for (let val of this.getExpressionValues()) {
            if (val.valueType === ExpressionValueType.SUBEXPRESSION && val.subExpression) {
                values.push(...val.subExpression.getAllExpressionValues());
            }
        }

        return values;
    }

    createExpressionValue(name: string, createValueType: IDataDefinition) {
        this.getExpressionValue(name, true, createValueType);
    }

    getExpressionValue(
        name: string,
        createIfMissing: boolean = true,
        createValueType: IDataDefinition | null = null,
    ): ExpressionValue | null {
        for (let v of this.values) {
            if (v.name === name) {
                return v;
            }
        }

        if (createIfMissing) {
            let v = new ExpressionValue();
            v.name = name;
            if (createValueType) v.expectedType = createValueType;

            this.values.push(v);
            return v;
        }

        return null;
    }

    registerExpressionValue(expressionValue: ExpressionValue): void {
        this.values.push(expressionValue);
        if (this.expressionContext) {
            this.expressionContext.register(expressionValue, this);
            expressionValue.setExpressionContext(this.expressionContext, this);
        }
    }

    // this method can be overridden
    getAllVariables(): string[] {
        let output: string[] = [];

        if (this.values) {
            for (let f of this.values) {
                output.push(...f.getAllVariables());
            }
        }

        return output;
    }

    // most expressions only have expression values.  The default implementation look for these expresssion values and read/write
    // this data.  These methods can be overridden for other storage.
    getStorageData(): object {
        let data = {};

        Reflect.set(data, "__type", this.__type);

        for (let fieldName of Object.getOwnPropertyNames(this)) {
            let d = Reflect.get(this, fieldName);

            if (d instanceof ExpressionValue) {
                Reflect.set(data, fieldName, d.getStorageData());
            }
        }

        return data;
    }

    setStorageData(data: object): void {
        for (let fieldName of Object.getOwnPropertyNames(this)) {
            let d = Reflect.get(this, fieldName);

            if (d instanceof ExpressionValue) {
                let evData = Reflect.get(data, fieldName);
                if (evData) {
                    d.setStorageData(evData);
                }
            }
        }
    }

    getValidationIssues(): ValidationIssue[] {
        return this.getExpressionValues().reduce((result: ValidationIssue[], current) => {
            result = [...result, ...current.getValidationIssues()];
            return result;
        }, []);
    }

    getVariableNames(): string[] {
        let variables: string[] = [];
        for (const value of this.values) {
            const variableNames = value.getVariableNames();
            if (variableNames && variableNames.length > 0) {
                variables = variables.concat(variableNames);
            }
        }
        const elements: Array<ExpressionValue> = Reflect.get(this, "elements") as Array<ExpressionValue>;
        if (elements) {
            for (const value of elements) {
                const variableNames = value.getVariableNames();
                if (variableNames && variableNames.length > 0) {
                    variables = variables.concat(variableNames);
                }
            }
        }
        return variables;
    }
}
